import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { preferencesAPI, organisationAPI, connectAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Settings as SettingsIcon,
  Mail,
  Building2,
  Bell,
  CreditCard,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Loader2,
  Unlink,
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const Settings = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [org, setOrg] = useState(null);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Stripe Connect state
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);
  
  // Handle Stripe Connect return
  useEffect(() => {
    const stripeReturn = searchParams.get('stripe_return');
    const stripeRefresh = searchParams.get('stripe_refresh');
    
    if (stripeReturn === 'true') {
      verifyStripeAccount();
      // Clean up URL
      window.history.replaceState({}, '', '/landlord/settings');
    } else if (stripeRefresh === 'true') {
      toast.info('Please complete your Stripe account setup');
      window.history.replaceState({}, '', '/landlord/settings');
    }
  }, [searchParams]);

  const loadSettings = async () => {
    try {
      const [prefsRes, orgRes, connectRes] = await Promise.all([
        preferencesAPI.get(),
        organisationAPI.getMe().catch(() => null),
        connectAPI.getStatus().catch(() => null),
      ]);
      setWeeklyDigest(prefsRes.data.weekly_digest);
      if (orgRes) setOrg(orgRes.data);
      if (connectRes) setConnectStatus(connectRes.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };
  
  const verifyStripeAccount = async () => {
    setVerifying(true);
    try {
      const response = await connectAPI.refreshStatus();
      setConnectStatus({
        connected: response.data.status === 'active',
        status: response.data.status,
        details: {
          charges_enabled: response.data.charges_enabled,
          payouts_enabled: response.data.payouts_enabled,
        },
        requirements: response.data.requirements || {},
      });
      
      if (response.data.status === 'active') {
        toast.success('Stripe account is now active! You can receive rent payments.');
      } else if (response.data.status === 'pending') {
        const pastDue = response.data.requirements?.past_due || [];
        if (pastDue.length > 0) {
          toast.warning('Your Stripe account needs additional verification. Click "Continue Setup" to complete it.');
        } else {
          toast.info('Your Stripe account is still pending verification. Stripe is reviewing your information.');
        }
      } else if (response.data.status === 'incomplete') {
        toast.warning('Your Stripe account setup is incomplete. Please complete the onboarding.');
      }
    } catch (error) {
      toast.error('Failed to refresh Stripe account status');
    } finally {
      setVerifying(false);
    }
  };
  
  const handleConnectStripe = async () => {
    setConnectLoading(true);
    try {
      const response = await connectAPI.createOnboarding();
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Stripe setup');
      setConnectLoading(false);
    }
  };
  
  const handleDisconnectStripe = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Stripe account? Tenants will not be able to pay rent until you reconnect.')) {
      return;
    }
    
    setConnectLoading(true);
    try {
      await connectAPI.disconnect();
      setConnectStatus({ connected: false, status: 'not_connected' });
      toast.success('Stripe account disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Stripe account');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleToggleDigest = async (checked) => {
    setWeeklyDigest(checked);
    setSaving(true);
    try {
      await preferencesAPI.update({ weekly_digest: checked });
      toast.success(checked ? 'Weekly digest enabled' : 'Weekly digest disabled');
    } catch (error) {
      setWeeklyDigest(!checked);
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };
  
  const getConnectStatusBadge = () => {
    if (!connectStatus) return null;
    
    const { status } = connectStatus;
    
    if (status === 'active') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
          <CheckCircle className="w-3.5 h-3.5" />
          Connected
        </span>
      );
    }
    
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Pending Verification
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs uppercase tracking-wider bg-red-500/10 text-red-400 border border-red-500/20">
        <AlertCircle className="w-3.5 h-3.5" />
        Not Connected
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout type="landlord" title={t('settings.title')}>
        <div className="space-y-6">
          {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-card animate-pulse" />)}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="landlord" title="Settings">
      <div className="max-w-2xl space-y-8" data-testid="settings-page">
        <div>
          <h2 className="font-serif text-2xl mb-1">{t('settings.title')}</h2>
          <p className="text-muted-foreground">{t('settings.subtitle')}</p>
        </div>

        {/* Stripe Connect - Payments */}
        <Card className="bg-card border-border" data-testid="stripe-connect-card">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-serif text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-gold" />
                Payment Account
              </span>
              {getConnectStatusBadge()}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {verifying ? (
              <div className="flex items-center gap-3 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Verifying your Stripe account...</span>
              </div>
            ) : connectStatus?.status === 'active' ? (
              <>
                <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-emerald-400">Your Stripe account is connected</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Rent payments from your tenants will be deposited directly into your Stripe account.
                        You can manage payouts and view transactions in your Stripe Dashboard.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-2">
                  <a 
                    href="https://dashboard.stripe.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-gold hover:text-gold-light transition-colors"
                  >
                    Open Stripe Dashboard
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDisconnectStripe}
                    disabled={connectLoading}
                    className="text-muted-foreground hover:text-red-400"
                    data-testid="disconnect-stripe-btn"
                  >
                    <Unlink className="w-4 h-4 mr-1.5" />
                    Disconnect
                  </Button>
                </div>
              </>
            ) : connectStatus?.status === 'pending' ? (
              <>
                <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Loader2 className="w-5 h-5 text-amber-400 mt-0.5 shrink-0 animate-spin" />
                    <div>
                      <p className="font-medium text-amber-400">Account verification in progress</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Stripe is reviewing your account information. 
                        {connectStatus?.requirements?.disabled_reason 
                          ? ' Your account has been paused due to missing requirements.'
                          : ' This usually takes a few minutes, but may take up to 24 hours.'}
                      </p>
                      
                      {/* Show specific requirements */}
                      {connectStatus?.requirements?.past_due?.length > 0 && (
                        <div className="mt-3 p-3 bg-red-500/5 border border-red-500/20 rounded">
                          <p className="text-sm font-medium text-red-400 mb-1">Action Required:</p>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            {connectStatus.requirements.past_due.map((req, i) => (
                              <li key={i} className="flex items-center gap-1.5">
                                <AlertCircle className="w-3 h-3 text-red-400 shrink-0" />
                                {req.replace(/\./g, ' > ').replace(/_/g, ' ')}
                              </li>
                            ))}
                          </ul>
                          <p className="text-xs text-muted-foreground mt-2">
                            Click "Continue Setup" below to complete these requirements in Stripe.
                          </p>
                        </div>
                      )}
                      
                      {connectStatus?.requirements?.currently_due?.length > 0 && !connectStatus?.requirements?.past_due?.length && (
                        <div className="mt-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded">
                          <p className="text-sm font-medium text-amber-400 mb-1">Pending Items:</p>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            {connectStatus.requirements.currently_due.map((req, i) => (
                              <li key={i} className="flex items-center gap-1.5">
                                <AlertCircle className="w-3 h-3 text-amber-400 shrink-0" />
                                {req.replace(/\./g, ' > ').replace(/_/g, ' ')}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <div className="mt-2 text-xs text-muted-foreground">
                        <p>Charges enabled: {connectStatus?.details?.charges_enabled ? '✅' : '❌'}</p>
                        <p>Payouts enabled: {connectStatus?.details?.payouts_enabled ? '✅' : '❌'}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 pt-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={verifyStripeAccount}
                    disabled={verifying}
                    className="bg-gold text-white hover:bg-gold-light"
                    data-testid="refresh-status-btn"
                  >
                    {verifying ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Refresh Status
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleConnectStripe}
                    disabled={connectLoading}
                    data-testid="complete-setup-btn"
                  >
                    {connectLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" />
                    )}
                    Continue Setup
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="p-4 bg-muted/30 border border-border rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium">Connect your Stripe account to receive rent payments</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Set up Stripe to receive rent payments directly from your tenants.
                        Payments are deposited into your bank account automatically.
                        No platform fees on rent payments.
                      </p>
                    </div>
                  </div>
                </div>
                
                <Button
                  onClick={handleConnectStripe}
                  disabled={connectLoading}
                  className="bg-gold text-white hover:bg-gold-light"
                  data-testid="connect-stripe-btn"
                >
                  {connectLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Connect Stripe Account
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Organisation Info */}
        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-gold" />
              {t('settings.organisation')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Name</p>
                <p className="font-medium">{org?.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Owner</p>
                <p className="font-medium">{user?.name}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Plan</p>
                <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-gold/10 text-gold border border-gold/20">
                  {org?.subscription_tier || 'BASIC'}
                </span>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Status</p>
                <span className="px-2 py-0.5 text-xs uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {org?.subscription_status || 'trial'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account */}
        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-gold" />
              {t('settings.account')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Email</p>
                <p className="font-medium">{user?.email}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Role</p>
                <p className="font-medium">{user?.role}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-gold" />
              {t('settings.notifications')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Weekly Digest */}
            <div className="flex items-start justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gold/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Mail className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <Label className="text-base font-medium cursor-pointer" htmlFor="weekly-digest">
                    Weekly Analytics Digest
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md">
                    Receive a weekly email every Monday with your portfolio summary:
                    MRR, active leases, occupancy rate, overdue payments, and open maintenance tickets.
                  </p>
                </div>
              </div>
              <Switch
                id="weekly-digest"
                checked={weeklyDigest}
                onCheckedChange={handleToggleDigest}
                disabled={saving}
                className="data-[state=checked]:bg-gold"
                data-testid="weekly-digest-toggle"
              />
            </div>

            <div className="border-t border-border pt-4">
              <p className="text-xs text-muted-foreground">
                Transactional emails (payment confirmations, late fee notices, reminders) are always sent regardless of this setting.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
